import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatMd, type DashboardPeriod, type SalesSeriesPoint } from '@gc/shared';

/**
 * Device and cafe sales over the selected period (SPEC §5.1, "Device sales
 * chart toggleable per day or per month").
 *
 * **Bars, not a line.** The server gap-fills the series but does not promise a
 * length: a day-toggled chart may be a handful of buckets and a month-toggled
 * one a single bucket. A line or area chart draws nothing visible for a series
 * of one, so bars are the only mark that reads correctly across the whole range
 * the contract allows.
 *
 * The two series are PAIRED side by side (approved dashboard card), because
 * the split is the question this chart answers — the combined figure already
 * leads the KPI row above it, so stacking here would say the same thing twice
 * while making the cafe series unreadable on a device-heavy day.
 */
export interface SalesChartProps {
  series: readonly SalesSeriesPoint[];
  period: DashboardPeriod;
  currencySymbol: string;
}

/** Colour tokens, never hex: a venue re-brands by overriding these variables. */
const DEVICE_COLOR = 'var(--brand-primary)';
const CAFE_COLOR = 'var(--brand-secondary)';
const AXIS_COLOR = 'var(--border-subtle)';
const TICK_COLOR = 'var(--text-muted)';
/** Axis numerals in the display face (card `.axis`/`.g span`: 9.5px mono). */
const TICK_STYLE = { fill: TICK_COLOR, fontSize: 10, fontFamily: 'var(--font-numerals)' };

/**
 * Compact axis labels ("2.5K", "1.2M").
 *
 * Deliberately formatted `en` in every language, for the same reason theme.css
 * pins `.tabular` to Western digits: staff read the incumbent system's receipts
 * and were trained on `10K`. Localised compact notation spells the magnitude out
 * as a word (`ar` gives "10 ألف"), which is both a different reading habit and
 * far too wide for a 56px axis gutter.
 */
function compactNumber(value: number): string {
  return new Intl.NumberFormat('en-u-nu-latn', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * A bucket key rendered for the axis.
 *
 * Keys are `YYYY-MM-DD` in DAY mode and `YYYY-MM` in MONTH mode (see
 * `buildSalesSeries`). Long series drop to the day number so 31 ticks still fit;
 * short ones keep enough context to be unambiguous.
 */
function tickLabel(key: string, period: DashboardPeriod, dense: boolean): string {
  if (period === 'MONTH') return key;
  return dense ? key.slice(8, 10) : key.slice(5);
}

export function SalesChart({ series, period, currencySymbol }: SalesChartProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const isRtl = i18n.dir() === 'rtl';
  const dense = series.length > 10;

  const money = (millidinars: string) => formatMd(millidinars, { currencySymbol, locale });

  if (series.length === 0) {
    return (
      <p className="grid h-64 place-items-center text-sm text-content-muted">
        {t('dashboard.chart.noBuckets')}
      </p>
    );
  }

  return (
    <>
      {/* The legend is plain HTML rather than recharts' <Legend>, so it inherits
          the page's logical-property layout and mirrors under RTL for free. */}
      <ul className="mb-2 flex flex-wrap items-center gap-4 text-xs text-content-secondary">
        <LegendSwatch color={DEVICE_COLOR} label={t('dashboard.chart.deviceSales')} />
        <LegendSwatch color={CAFE_COLOR} label={t('dashboard.chart.cafeSales')} />
      </ul>

      {/*
        Forced LTR container. Recharts positions its tooltip wrapper with inline
        `left`/`top` in pixels measured from the container's physical left edge;
        under `direction: rtl` those offsets are applied from the opposite side
        and the tooltip detaches from the cursor. Pinning the container to LTR
        keeps that maths honest, and the axes below are mirrored explicitly so
        the chart still reads right-to-left in Arabic and Kurdish.
      */}
      <div dir="ltr" className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={[...series]} margin={{ top: 4, right: 8, bottom: 4, left: 8 }} barGap={3}>
            <XAxis
              dataKey="key"
              // Mirrors the time axis for RTL readers: newest bucket on the left.
              reversed={isRtl}
              stroke={AXIS_COLOR}
              tick={TICK_STYLE}
              tickLine={false}
              interval="preserveStartEnd"
              tickFormatter={(value: string) => tickLabel(value, period, dense)}
            />
            <YAxis
              // The value axis moves to the reader's leading edge under RTL.
              orientation={isRtl ? 'right' : 'left'}
              stroke={AXIS_COLOR}
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              width={44}
              tickFormatter={(value: number) => compactNumber(value)}
            />
            <Tooltip
              cursor={{ fill: 'var(--surface-hover)' }}
              content={({ active, label }) => {
                if (!active) return null;
                const point = series.find((entry) => entry.key === String(label));
                if (!point) return null;

                return (
                  <div className="rounded-control border border-subtle bg-overlay px-3 py-2 text-xs shadow-lg">
                    <p className="mb-1 font-medium text-content">{point.key}</p>
                    <TooltipRow
                      color={DEVICE_COLOR}
                      label={t('dashboard.chart.deviceSales')}
                      value={money(point.deviceSalesMd)}
                    />
                    <TooltipRow
                      color={CAFE_COLOR}
                      label={t('dashboard.chart.cafeSales')}
                      value={money(point.cafeSalesMd)}
                    />
                    <p className="mt-1 border-t border-subtle pt-1 font-medium text-content">
                      {t('common.total')} <span className="tabular">{money(point.totalMd)}</span>
                    </p>
                  </div>
                );
              }}
            />
            {/* `*Iqd` floats are the plotted magnitudes only. Every figure the
                owner reads comes from the `*Md` strings via formatMd. */}
            <Bar dataKey="deviceSalesIqd" fill={DEVICE_COLOR} radius={[3, 3, 0, 0]} />
            <Bar dataKey="cafeSalesIqd" fill={CAFE_COLOR} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <li className="flex items-center gap-1.5">
      <span
        className="inline-block h-2.5 w-2.5 rounded-sm"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      {label}
    </li>
  );
}

function TooltipRow({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <p className="flex items-center gap-2 text-content-secondary">
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-sm"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span>{label}</span>
      <span className="tabular ms-auto text-content">{value}</span>
    </p>
  );
}
